import { BoardEngine } from '../board/engine';
import { audio } from '../audio/synth';
import { loadSave, markTutorialSeen, recordLevelResult, resetSave, updateSettings, writeSave } from '../core/save';
import type { Coord, GameSettings, SaveData, WorldTheme } from '../core/types';
import { InputManager } from '../input/inputManager';
import { CAMPAIGN, getLevel, WORLDS } from '../levels/campaign';
import { BoardView } from '../render/boardView';
import { GameScene } from '../render/scene';
import { UI } from '../ui/ui';

type AppMode = 'menu' | 'playing' | 'paused';

export class AetherloomGame {
  private scene: GameScene;
  private boardView: BoardView;
  private ui: UI;
  private input: InputManager;
  private engine: BoardEngine | null = null;
  private save: SaveData;
  private mode: AppMode = 'menu';
  private currentLevelId = 1;
  private raf = 0;
  private lastT = 0;
  private time = 0;

  constructor(canvas: HTMLCanvasElement, uiRoot: HTMLElement) {
    this.save = loadSave();
    this.scene = new GameScene(canvas);
    this.boardView = new BoardView();
    this.scene.boardAnchor.add(this.boardView.root);

    this.ui = new UI(uiRoot, this.save, {
      onPlay: (id) => this.startLevel(id),
      onContinue: () => {
        const id = this.save.lastPlayedLevel ?? this.save.unlockedLevel;
        this.startLevel(Math.min(id, 50));
      },
      onOpenMap: () => {
        this.mode = 'menu';
        this.ui.show('map');
        this.ui.renderMap();
      },
      onOpenSettings: () => this.ui.show('settings'),
      onOpenCredits: () => this.ui.show('credits'),
      onBackMain: () => {
        this.mode = 'menu';
        this.ui.show('main');
      },
      onPause: () => this.pause(),
      onResume: () => this.resume(),
      onRestart: () => this.startLevel(this.currentLevelId),
      onNextLevel: () => {
        const next = this.currentLevelId + 1;
        if (next <= 50) this.startLevel(next);
        else {
          this.mode = 'menu';
          this.ui.show('map');
        }
      },
      onQuitLevel: () => {
        this.engine = null;
        this.mode = 'menu';
        this.ui.show('map');
        this.input.setEnabled(false);
      },
      onHint: () => this.showHint(),
      onSettingsChange: (s) => this.applySettings(s),
      onResetProgress: () => {
        this.save = resetSave();
        this.ui.updateSave(this.save);
      },
      onTutorialDismiss: () => {
        this.ui.showHudOnly();
        this.input.setEnabled(true);
        this.mode = 'playing';
      },
    });

    this.input = new InputManager(canvas, this.scene, this.boardView, {
      onSelect: (c) => this.handleSelect(c),
      onSwap: (a, b) => this.handleSwap(a, b),
      onHover: (c) => {
        this.boardView.setHover(c);
      },
    });
    this.input.setEnabled(false);

    this.applySettings(this.save.settings);
    this.ui.show('main');
    this.scene.applyTheme('ember');
    this.showTitleShowcase();

    // Unlock audio on first interaction
    const unlock = async () => {
      await audio.ensure();
      audio.applySettings(this.save.settings);
      audio.startMusic('ember');
      window.removeEventListener('pointerdown', unlock);
    };
    window.addEventListener('pointerdown', unlock);

    this.loop(0);

    // Dev / QA inspection surface
    (window as unknown as { __aetherloom: AetherloomGame }).__aetherloom = this;
  }

  private showTitleShowcase(): void {
    // Lightweight decorative board for the title screen
    const level = getLevel(1);
    if (!level) return;
    this.engine = new BoardEngine(level, 42);
    this.boardView.setAccessibility({
      colorblind: this.save.settings.colorblind,
      highContrast: this.save.settings.highContrast,
      reducedMotion: this.save.settings.reducedMotion,
      reducedFlash: this.save.settings.reducedFlash,
    });
    this.boardView.bind(this.engine, 'ember');
    this.input.setEnabled(false);
  }

  /** Headless helper for critics / automation */
  getEngine(): BoardEngine | null {
    return this.engine;
  }

  forceUnlockAll(): void {
    this.save.unlockedLevel = 50;
    writeSave(this.save);
    this.ui.updateSave(this.save);
  }

  private applySettings(partial: Partial<GameSettings>): void {
    this.save = updateSettings(partial);
    this.ui.updateSave(this.save);
    audio.applySettings(this.save.settings);
    this.scene.setQuality(this.save.settings.quality);
    this.scene.setReducedMotion(this.save.settings.reducedMotion);
    this.boardView.setAccessibility({
      colorblind: this.save.settings.colorblind,
      highContrast: this.save.settings.highContrast,
      reducedMotion: this.save.settings.reducedMotion,
      reducedFlash: this.save.settings.reducedFlash,
    });
    if (this.engine) this.boardView.syncAll();
  }

  private themeForLevel(levelId: number): WorldTheme {
    const world = WORLDS.find((w) => w.levelIds.includes(levelId));
    return world?.theme ?? 'ember';
  }

  startLevel(levelId: number): void {
    const level = getLevel(levelId);
    if (!level) return;
    this.currentLevelId = levelId;
    this.save.lastPlayedLevel = levelId;
    writeSave(this.save);

    const theme = this.themeForLevel(levelId);
    this.scene.applyTheme(theme);
    audio.startMusic(theme);

    this.engine = new BoardEngine(level);
    this.boardView.setAccessibility({
      colorblind: this.save.settings.colorblind,
      highContrast: this.save.settings.highContrast,
      reducedMotion: this.save.settings.reducedMotion,
      reducedFlash: this.save.settings.reducedFlash,
    });
    this.boardView.bind(this.engine, theme);
    this.wireEngine(this.engine);

    this.ui.updateHud(this.engine.movesLeft, this.engine.score, this.engine.objectives);
    this.ui.showHudOnly();

    const tutKey = `level_${levelId}`;
    if (level.tutorial?.length && !this.save.tutorialsSeen.includes(tutKey)) {
      this.input.setEnabled(false);
      this.ui.showTutorial(level.name, level.tutorial);
      markTutorialSeen(tutKey);
      this.save = loadSave();
    } else {
      this.input.setEnabled(true);
      this.mode = 'playing';
    }
  }

  private engineUnsub: (() => void) | null = null;

  private wireEngine(engine: BoardEngine): void {
    if (this.engineUnsub) {
      this.engineUnsub();
      this.engineUnsub = null;
    }
    this.engineUnsub = engine.bus.on('board', (ev: unknown) => {
      const e = ev as { type: string; payload: Record<string, unknown> };
      switch (e.type) {
        case 'swap':
          audio.swap();
          break;
        case 'swap_invalid':
          audio.invalid();
          break;
        case 'match':
        case 'cascade':
          audio.cascade(Number(e.payload.depth ?? 1));
          break;
        case 'special_created':
          audio.specialCreate();
          break;
        case 'special_activated':
          audio.specialActivate();
          this.scene.pulseCamera(0.12);
          break;
        case 'special_combo':
          audio.combo();
          this.scene.pulseCamera(0.22);
          break;
        case 'blocker_damaged':
          audio.blocker();
          break;
        case 'objective_progress':
          audio.objective();
          break;
        case 'score':
        case 'phase':
          break;
        case 'win':
          audio.win();
          this.onWin();
          break;
        case 'lose':
          audio.lose();
          this.onLose();
          break;
      }
      if (this.engine) {
        this.ui.updateHud(this.engine.movesLeft, this.engine.score, this.engine.objectives);
        this.boardView.setSelected(this.engine.selected);
        this.input.setSelected(this.engine.selected);
        this.input.setEnabled(this.engine.canInput && this.mode === 'playing');
      }
    });
  }

  private handleSelect(c: Coord): void {
    if (!this.engine || this.mode !== 'playing') return;
    void audio.ensure();
    const before = this.engine.selected;
    this.engine.select(c);
    if (this.engine.selected) audio.select();
    this.boardView.setSelected(this.engine.selected);
    this.input.setSelected(this.engine.selected);
    // if reselect vs swap handled inside engine
    void before;
  }

  private handleSwap(a: Coord, b: Coord): void {
    if (!this.engine || this.mode !== 'playing') return;
    void audio.ensure();
    this.engine.trySwap(a, b);
    this.boardView.setSelected(this.engine.selected);
    this.input.setSelected(this.engine.selected);
  }

  private showHint(): void {
    if (!this.engine || !this.save.settings.hints) return;
    const h = this.engine.getHint();
    if (h) {
      this.boardView.flashHint(h.a, h.b);
      audio.ui();
    }
  }

  private pause(): void {
    if (this.mode !== 'playing') return;
    this.mode = 'paused';
    this.input.setEnabled(false);
    this.ui.show('pause');
  }

  private resume(): void {
    if (this.mode !== 'paused') return;
    this.mode = 'playing';
    this.ui.showHudOnly();
    this.input.setEnabled(this.engine?.canInput ?? false);
  }

  private onWin(): void {
    if (!this.engine) return;
    this.mode = 'menu';
    this.input.setEnabled(false);
    const stars = this.engine.computeStars();
    const nextUnlock = Math.min(50, this.currentLevelId + 1);
    this.save = recordLevelResult(
      this.currentLevelId,
      stars,
      this.engine.score,
      this.engine.movesLeft,
      // unlock next if we beat current
      Math.max(this.save.unlockedLevel, nextUnlock)
    );
    // ensure unlock
    if (this.save.unlockedLevel < nextUnlock) {
      this.save.unlockedLevel = nextUnlock;
      writeSave(this.save);
    }
    this.ui.updateSave(this.save);
    this.ui.showWin(this.engine.score, this.engine.movesLeft, stars);
  }

  private onLose(): void {
    if (!this.engine) return;
    this.mode = 'menu';
    this.input.setEnabled(false);
    this.ui.showLose(this.engine.score);
  }

  private loop = (t: number): void => {
    const dt = Math.min(0.05, (t - this.lastT) / 1000 || 0.016);
    this.lastT = t;
    this.time += dt;
    if (this.mode === 'playing' || this.mode === 'paused' || this.mode === 'menu') {
      this.boardView.update(dt, this.time);
      this.scene.update(this.time);
    }
    this.scene.render();
    this.raf = requestAnimationFrame(this.loop);
  };
}
